import Close from '@material-ui/icons/Close';
import { BodyMBold, Caption, CaptionBold, HeadingLBold } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import InfoCard from 'components/Info/InfoCard';
import moment from 'moment';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import numeral from 'numeral';
import SimpleTable from 'components/SimpleTable/SimpleTable';
import { SinglePayment } from 'utils/api/models/payments/singlePayment';
import { AccountCell, PhoneNumberCell, UserNameCell } from 'components/Cells/Cells';
import CustomAvatar from 'components/CustomAvatar/CustomAvatar';
import { IColumn } from 'components/SimpleTable/Row';
import React from 'react';
import { makeStyles } from '@material-ui/core';
import styles from 'assets/jss/material-dashboard-pro-react/views/payments/historyStyle';
import Business from '@material-ui/icons/Business';
import { PaymentDetails } from 'utils/api/models/payments/paymentDetails';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IPaymentDetailsCardProps {
	paymentDetails: PaymentDetails | null;
	isCorporate: boolean;
	closePaymentDetails: () => void;
	accountsMap: { [accountId: string]: SingleCorpAccount };
}

const PaymentDetailsCard = ({
	paymentDetails,
	isCorporate,
	closePaymentDetails,
	accountsMap,
}: IPaymentDetailsCardProps) => {
	const classes = useStyles();

	const TableHeader = ({ children }: { children: React.ReactElement | string }) => (
		<div className={classes.cell}>
			<Caption color="secondary-light" disableMargin>
				{children}
			</Caption>
		</div>
	);

	const Cell = ({ children }: { children: React.ReactElement }) => <div className={classes.cell}>{children}</div>;

	return (
		<ConditionalRender
			show={!!paymentDetails}
			onTrue={() => (
				<div className={classes.paymentDetailsContainer}>
					<div className={classes.paymentDetailsHeaderContainer}>
						<Close className={classes.closeIcon} onClick={closePaymentDetails} />
						<div className={classes.paymentDetailsHeader}>
							<HeadingLBold color="primary-light">{translate('top.up.many')}</HeadingLBold>
						</div>
					</div>
					<div className={classes.paymentDetails}>
						<GridContainer justify="flex-start">
							<GridItem xs={12} lg={9}>
								<InfoCard
									title={translate('payment.details')}
									elements={[
										{
											title: translate('date'),
											value: moment().format('hh:mm A DD.MM.YYYY'),
										},
										{
											title: translate('service.type'),
											value: translate('top.up.many'),
										},
									]}
								/>
							</GridItem>
						</GridContainer>
						<Card>
							<CardBody>
								<GridContainer className={classes.generalPaymentDetails}>
									<GridItem xs={12} sm={6}>
										<Caption>{translate('payments.topup.numbersAmount')}</Caption>
										<CaptionBold className={classes.numbersAmount}>
											{String(paymentDetails?.items?.length)}
										</CaptionBold>
									</GridItem>
									<GridItem xs={12} sm={6}>
										<Caption>{translate('payments.topup.totalAmount')}</Caption>
										<div>
											<BodyMBold color="primary-light" inline>
												<>
													{numeral(
														paymentDetails?.items?.reduce(
															(acc: number, item: any) => item.amount + acc,
															0
														)
													).format('0,0.000')}
												</>
											</BodyMBold>
											<CaptionBold color="primary-light" inline>
												{translate('OMR')}
											</CaptionBold>
										</div>
									</GridItem>
								</GridContainer>
							</CardBody>
						</Card>
						<SimpleTable
							idAccessor="requestId"
							columns={
								([
									{
										header: <TableHeader>{translate('phone.number')}</TableHeader>,
										accessor: 'phoneNumber',
										width: 100,
										cell: ({ phoneNumber }: SinglePayment) => (
											<Cell>
												<PhoneNumberCell text={phoneNumber || ''} />
											</Cell>
										),
									},
									{
										width: 200,
										header: <TableHeader>{translate('user.name')}</TableHeader>,
										cell: ({ username }: SinglePayment) => (
											<Cell>
												<UserNameCell text={username || ''} />
											</Cell>
										),
									},
									!isCorporate && {
										width: 200,
										header: <TableHeader>{translate('organization.details')}</TableHeader>,
										cell: ({ accountId }: SinglePayment) => (
											<Cell>
												<AccountCell
													text={!!accountId ? accountsMap[accountId]!.accountName : ''}
													icon={
														<CustomAvatar
															placeholderIcon={<Business />}
															accountId={accountId}
															customClasses={classes.inlineIcon}
															alt="avatar"
														/>
													}
												/>
											</Cell>
										),
									},
									{
										width: 80,
										header: <TableHeader>{translate('amount')}</TableHeader>,
										cell: ({ amount }: SinglePayment) => (
											<Cell>
												<>
													<CaptionBold inline>
														{numeral(amount).format('0,0.000')}
													</CaptionBold>
													<Caption inline> OMR</Caption>
												</>
											</Cell>
										),
									},
								].filter((column) => !!column) as unknown) as Array<IColumn>
							}
							data={(paymentDetails as any).items!}
							selectedItems={[]}
							setSelectedItems={() => null}
						/>
					</div>
				</div>
			)}
		/>
	);
};

export default PaymentDetailsCard;
